// When it is worth asking the server whether an open task has changed.
//
// The task modal never refreshed itself. A patient could reply, or book through
// their own link, and the staff member with the task open would not know until
// they closed it and opened it again — which is exactly what they had no reason
// to do, because nothing on screen suggested anything had happened.
//
// Polling rather than sockets: socketService has a 'triage' room and a
// broadcastToRoom, but nothing has ever emitted to either, and whether
// websockets survive the production deploy is still an open question in the
// handoff. A socket that silently never connects is worse than a poll, because
// staff would trust it.
//
// Separated from the modal so the rules are readable on their own, and so they
// can be tested without mounting a dialog with a hundred hooks in it.

export interface PollConditions {
  /** The modal is open on a task. */
  isOpen: boolean;
  /** The browser tab is in the background. */
  hidden: boolean;
  /** A send, confirm or save the user started is still in flight. */
  busy: boolean;
}

// Every reason not to ask, in one place.
export function shouldPoll({ isOpen, hidden, busy }: PollConditions): boolean {
  if (!isOpen) return false;
  // A backgrounded tab has nobody looking at it. The request would cost the same
  // and be read by no one.
  if (hidden) return false;
  // Never race the user's own action: a poll landing mid-send would paint the
  // task as it was before their send and make it look like nothing happened.
  if (busy) return false;
  return true;
}

// Long enough not to be a second board poll, short enough that a reply arriving
// while you type is visible before you finish the sentence. The board polls at
// 20s and costs 2-4s a time; this costs about 570ms.
export const TASK_POLL_MS = 15000;

export interface TaskShape {
  status?: string;
  conversationMessages?: unknown[];
  internalNotes?: unknown[];
}

// What changed between two versions of the same task, in words for the person
// looking at it. Empty string means nothing worth mentioning — which is the
// normal case, and must stay silent rather than announcing "no change".
export function describeTaskChange(before: TaskShape | null, after: TaskShape | null): string {
  if (!before || !after) return '';

  const newMessages = (after.conversationMessages?.length || 0) - (before.conversationMessages?.length || 0);
  const statusChanged = before.status && after.status && before.status !== after.status;

  const parts: string[] = [];
  if (newMessages > 0) {
    parts.push(newMessages === 1 ? '1 new message' : `${newMessages} new messages`);
  }
  if (statusChanged) {
    // Named plainly rather than as a status code: "inprogress" is not a word.
    parts.push(`moved to ${statusLabel(String(after.status))}`);
  }
  return parts.join(' · ');
}

export function statusLabel(status: string): string {
  switch (status) {
    case 'todo': return 'To Do';
    case 'inprogress': return 'In Progress';
    case 'completed': return 'Complete';
    default: return status;
  }
}

// How many messages at the end of the thread are new since the staff member
// last looked. Used to mark them, not to reorder anything.
export function newMessageCount(before: TaskShape | null, after: TaskShape | null): number {
  const had = before?.conversationMessages?.length || 0;
  const has = after?.conversationMessages?.length || 0;
  return Math.max(0, has - had);
}
